import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, CheckCircle, XCircle, Loader2, MessageSquare, Settings as SettingsIcon, Shuffle, Music, Upload, Download, Play } from 'lucide-react';

const Settings = () => {
  // SMS States
  const [smsProviders, setSmsProviders] = useState([]);
  const [isAddSmsOpen, setIsAddSmsOpen] = useState(false);
  const [testingSms, setTestingSms] = useState(null);
  const [newSmsProvider, setNewSmsProvider] = useState({
    name: '',
    provider_type: 'twilio',
    account_sid: '',
    auth_token: '',
    from_number: '',
    priority: 1,
  });

  // System Settings States
  const [systemSettings, setSystemSettings] = useState({
    randomize_sip: false,
    randomize_sms: false,
  });

  // Audio Files States
  const [audioFiles, setAudioFiles] = useState([]);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  useEffect(() => {
    fetchSmsProviders();
    fetchSystemSettings();
    fetchAudioFiles();
  }, []);

  const fetchSmsProviders = async () => {
    try {
      const response = await axios.get(`${API}/sms-providers`);
      setSmsProviders(response.data);
    } catch (error) {
      console.error('Error fetching SMS providers:', error);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/system-settings`);
      setSystemSettings(response.data);
    } catch (error) {
      console.error('Error fetching system settings:', error);
    }
  };

  const fetchAudioFiles = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/audio-files`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAudioFiles(response.data);
    } catch (error) {
      console.error('Error fetching audio files:', error);
    }
  };

  const handleUploadAudio = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/x-m4a'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|m4a)$/i)) {
      toast.error('Tipo de arquivo não suportado. Use MP3, WAV, OGG ou M4A');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace(/\.[^/.]+$/, '')); // Remove extension

    setUploadingAudio(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/audio-files/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
      });
      toast.success('Áudio enviado com sucesso!');
      await fetchAudioFiles();
      // Reset file input
      event.target.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar áudio');
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleDeleteAudio = async (audioId) => {
    if (!window.confirm('Tem certeza que deseja excluir este áudio?')) {
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/audio-files/${audioId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Áudio excluído');
      await fetchAudioFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir áudio');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // SMS Functions
  const handleAddSmsProvider = async () => {
    if (!newSmsProvider.name || !newSmsProvider.account_sid || !newSmsProvider.auth_token || !newSmsProvider.from_number) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      await axios.post(`${API}/sms-providers`, newSmsProvider);
      toast.success('Provedor SMS adicionado com sucesso');
      setNewSmsProvider({
        name: '',
        provider_type: 'twilio',
        account_sid: '',
        auth_token: '',
        from_number: '',
        priority: 1,
      });
      setIsAddSmsOpen(false);
      await fetchSmsProviders();
    } catch (error) {
      toast.error('Erro ao adicionar provedor SMS');
    }
  };

  const handleDeleteSmsProvider = async (providerId) => {
    if (!window.confirm('Tem certeza que deseja excluir este provedor?')) {
      return;
    }

    try {
      await axios.delete(`${API}/sms-providers/${providerId}`);
      toast.success('Provedor SMS excluído');
      await fetchSmsProviders();
    } catch (error) {
      toast.error('Erro ao excluir provedor');
    }
  };

  const handleTestSmsProvider = async (providerId) => {
    setTestingSms(providerId);
    try {
      const response = await axios.post(`${API}/sms-providers/${providerId}/test`);
      if (response.data.status === 'active') {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
      await fetchSmsProviders();
    } catch (error) {
      toast.error('Erro ao testar provedor');
    } finally {
      setTestingSms(null);
    }
  };

  // System Settings Functions
  const handleUpdateSettings = async (key, value) => {
    try {
      const updatedSettings = { ...systemSettings, [key]: value };
      await axios.patch(`${API}/system-settings`, updatedSettings);
      setSystemSettings(updatedSettings);
      toast.success('Configuração atualizada');
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    }
  };

  const getStatusIndicator = (status) => {
    switch (status) {
      case 'online':
      case 'active':
        return <CheckCircle size={20} className="text-green-600" />;
      case 'offline':
      case 'inactive':
        return <XCircle size={20} className="text-slate-400" />;
      case 'error':
        return <XCircle size={20} className="text-red-600" />;
      default:
        return <XCircle size={20} className="text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      online: { label: 'Online', className: 'bg-green-100 text-green-700' },
      active: { label: 'Ativo', className: 'bg-green-100 text-green-700' },
      offline: { label: 'Offline', className: 'bg-slate-100 text-slate-700' },
      inactive: { label: 'Inativo', className: 'bg-slate-100 text-slate-700' },
      error: { label: 'Erro', className: 'bg-red-100 text-red-700' },
    };

    const config = statusConfig[status] || statusConfig.offline;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Configurações</h1>
        <p className="text-slate-600">Gerencie provedores SMS, áudios personalizados e configurações do sistema</p>
      </div>

      <Tabs defaultValue="audio" className="space-y-6">
        <TabsList className="grid w-full grid-cols-1">
          {/* Hidden tabs - will be implemented later */}
          {false && (
            <>
              <TabsTrigger value="sms" className="flex items-center gap-2">
                <MessageSquare size={16} />
                Provedores SMS
              </TabsTrigger>
              <TabsTrigger value="system" className="flex items-center gap-2">
                <SettingsIcon size={16} />
                Sistema
              </TabsTrigger>
            </>
          )}
          <TabsTrigger value="audio" className="flex items-center gap-2">
            <Music size={16} />
            Meus Áudios
          </TabsTrigger>
        </TabsList>

        {/* SMS PROVIDERS TAB - Hidden for now, will be implemented later */}
        {false && (
          <TabsContent value="sms" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare size={20} className="text-teal-600" />
                    Provedores SMS
                  </CardTitle>
                  <CardDescription className="mt-2">
                    Configure Twilio, Vonage e outros provedores com failover automático
                  </CardDescription>
                </div>
                <Dialog open={isAddSmsOpen} onOpenChange={setIsAddSmsOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-teal-600 hover:bg-teal-700">
                      <Plus size={16} className="mr-2" />
                      Novo Provedor SMS
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Adicionar Provedor SMS</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Nome *</Label>
                          <Input
                            value={newSmsProvider.name}
                            onChange={(e) => setNewSmsProvider({ ...newSmsProvider, name: e.target.value })}
                            placeholder="Ex: Twilio Principal"
                          />
                        </div>
                        <div>
                          <Label>Provedor *</Label>
                          <Select
                            value={newSmsProvider.provider_type}
                            onValueChange={(value) => setNewSmsProvider({ ...newSmsProvider, provider_type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="twilio">Twilio</SelectItem>
                              <SelectItem value="vonage">Vonage</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label>Account SID / API Key *</Label>
                        <Input
                          value={newSmsProvider.account_sid}
                          onChange={(e) => setNewSmsProvider({ ...newSmsProvider, account_sid: e.target.value })}
                          placeholder="Ex: ACxxxxxxxxxxxxxxxxxxxx"
                        />
                      </div>

                      <div>
                        <Label>Auth Token / API Secret *</Label>
                        <Input
                          type="password"
                          value={newSmsProvider.auth_token}
                          onChange={(e) => setNewSmsProvider({ ...newSmsProvider, auth_token: e.target.value })}
                          placeholder="••••••••"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Número de Origem *</Label>
                          <Input
                            value={newSmsProvider.from_number}
                            onChange={(e) => setNewSmsProvider({ ...newSmsProvider, from_number: e.target.value })}
                            placeholder="+5511999999999"
                          />
                        </div>
                        <div>
                          <Label>Prioridade</Label>
                          <Select
                            value={newSmsProvider.priority.toString()}
                            onValueChange={(value) => setNewSmsProvider({ ...newSmsProvider, priority: parseInt(value) })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 - Mais Alta</SelectItem>
                              <SelectItem value="2">2 - Alta</SelectItem>
                              <SelectItem value="3">3 - Média</SelectItem>
                              <SelectItem value="4">4 - Baixa</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <Button onClick={handleAddSmsProvider} className="w-full">
                        Salvar Provedor SMS
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {smsProviders.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare size={48} className="mx-auto text-slate-400 mb-4" />
                  <p className="text-lg text-slate-600 mb-2">Nenhum provedor SMS configurado</p>
                  <p className="text-sm text-slate-500">Adicione Twilio ou Vonage para enviar SMS</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {smsProviders.map((provider) => (
                    <Card key={provider.id} className="border-2">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {getStatusIndicator(provider.status)}
                            <h3 className="font-semibold text-slate-800">{provider.name}</h3>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(provider.status)}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSmsProvider(provider.id)}
                              className="h-8 w-8 p-0 hover:bg-red-100"
                            >
                              <Trash2 size={14} className="text-red-600" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <span className="text-slate-600">Provedor:</span>
                            <span className="font-semibold text-slate-900 capitalize">{provider.provider_type}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <span className="text-slate-600">Prioridade:</span>
                            <span className="text-slate-900">{provider.priority}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <span className="text-slate-600">Número:</span>
                            <span className="font-mono text-slate-900">{provider.from_number}</span>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleTestSmsProvider(provider.id)}
                          disabled={testingSms === provider.id}
                          variant="outline"
                          className="w-full mt-4"
                        >
                          {testingSms === provider.id ? (
                            <>
                              <Loader2 size={16} className="mr-2 animate-spin" />
                              Testando...
                            </>
                          ) : (
                            'Testar Provedor'
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* AUDIO FILES TAB */}
        <TabsContent value="audio" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Music size={20} className="text-teal-600" />
                    Meus Áudios
                  </CardTitle>
                  <CardDescription className="mt-2">
                    Faça upload de áudios personalizados para usar na URA durante atendimentos
                  </CardDescription>
                </div>
                <div>
                  <input
                    type="file"
                    id="audio-upload"
                    accept="audio/mpeg,audio/wav,audio/ogg,audio/x-m4a,.mp3,.wav,.ogg,.m4a"
                    onChange={handleUploadAudio}
                    className="hidden"
                    disabled={uploadingAudio}
                  />
                  <Button
                    onClick={() => document.getElementById('audio-upload').click()}
                    className="bg-teal-600 hover:bg-teal-700"
                    disabled={uploadingAudio}
                  >
                    {uploadingAudio ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Upload Áudio
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {audioFiles.length === 0 ? (
                <div className="text-center py-12">
                  <Music size={48} className="mx-auto text-slate-400 mb-4" />
                  <p className="text-lg text-slate-600 mb-2">Nenhum áudio cadastrado</p>
                  <p className="text-sm text-slate-500">Faça upload de arquivos MP3, WAV, OGG ou M4A</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {audioFiles.map((audio) => (
                    <Card key={audio.id} className="border-2 hover:border-teal-200 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                              <Music size={20} className="text-teal-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-slate-900">{audio.name}</h4>
                              <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                <span>{formatFileSize(audio.file_size)}</span>
                                <span>•</span>
                                <span>{formatDate(audio.uploaded_at)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`${API}/audio-files/${audio.id}/download`, '_blank')}
                              className="h-8 hover:bg-green-50"
                            >
                              <Download size={14} className="text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAudio(audio.id)}
                              className="h-8 hover:bg-red-50"
                            >
                              <Trash2 size={14} className="text-red-600" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-blue-900 mb-2">Formatos Suportados</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p>• <strong>MP3</strong> - Formato mais comum, boa compressão</p>
                <p>• <strong>WAV</strong> - Alta qualidade, sem compressão</p>
                <p>• <strong>OGG</strong> - Código aberto, boa qualidade</p>
                <p>• <strong>M4A</strong> - Formato Apple, boa compressão</p>
              </div>
              <div className="mt-3 pt-3 border-t border-blue-300">
                <p className="text-sm text-blue-800">
                  <strong>Dica:</strong> Os áudios ficam disponíveis automaticamente na URA para reprodução durante atendimentos.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SYSTEM SETTINGS TAB - Hidden for now, will be implemented later */}
        {false && (
          <TabsContent value="system" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shuffle size={20} className="text-teal-600" />
                Configurações de Randomização e Failover
              </CardTitle>
              <CardDescription>
                Configure o comportamento automático do sistema para SIP e SMS
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Randomize SIP */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">Randomizar Contas SIP</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    Alterna automaticamente entre contas SIP disponíveis. Se uma falhar, usa a próxima.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    ✓ Distribuição de carga<br />
                    ✓ Failover automático<br />
                    ✓ Maximiza disponibilidade
                  </p>
                </div>
                <Switch
                  checked={systemSettings.randomize_sip}
                  onCheckedChange={(checked) => handleUpdateSettings('randomize_sip', checked)}
                  className="ml-4"
                />
              </div>

              {/* Randomize SMS */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <MessageSquare size={18} className="text-teal-600" />
                    <h3 className="font-semibold text-slate-900">Randomizar Provedores SMS</h3>
                  </div>
                  <p className="text-sm text-slate-600">
                    Alterna entre Twilio, Vonage e outros. Se um falhar, tenta o próximo automaticamente.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    ✓ Failover inteligente<br />
                    ✓ Segue ordem de prioridade (se desligado)<br />
                    ✓ Nunca perde um SMS
                  </p>
                </div>
                <Switch
                  checked={systemSettings.randomize_sms}
                  onCheckedChange={(checked) => handleUpdateSettings('randomize_sms', checked)}
                  className="ml-4"
                />
              </div>

              {/* Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <h4 className="font-semibold text-blue-900 mb-2">Como funciona o Failover</h4>
                    <p className="text-sm text-blue-800">
                      O sistema tenta cada provedor até que um funcione. A ordem depende da prioridade (ou aleatória se randomizar estiver ativo).
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-6">
                    <h4 className="font-semibold text-green-900 mb-2">Vantagens da Randomização</h4>
                    <p className="text-sm text-green-800">
                      Distribui a carga entre múltiplos provedores, evita limite de taxa e aumenta a confiabilidade do sistema.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* System Info */}
          <Card>
            <CardHeader>
              <CardTitle>Informações do Sistema</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-600">Versão:</span>
                  <span className="ml-2 font-semibold">1.0.0</span>
                </div>
                <div>
                  <span className="text-slate-600">Sistema:</span>
                  <span className="ml-2 font-semibold">VoIP Call Center</span>
                </div>
                <div>
                  <span className="text-slate-600">Provedores SMS:</span>
                  <span className="ml-2 font-semibold">{smsProviders.length}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Settings;
